import { ref as dbRef, onValue } from 'firebase/database';
import {
  clueAt, cellsOf, letterAt, wrongCells, whiteCells,
} from '@/engine/solving';

// Everything a bug report could conceivably need, gathered at send time.
//
// Matt, 2026-10-10: "We should generally look across all the apps and make
// sure that we are always passing in any information that might or even
// remotely could be relevant to any bug report." Until then a crossword
// report carried only `{ date, roomId, solved }` - no cursor, no clue, no
// progress, no settings, no idea whether the phone was even online.
//
// Rules for what goes in: compact (counts, never the grid or the answers),
// no secrets or tokens, and no other solvers' emails - names and colours
// only. And every piece is gathered behind its own try/catch: a snapshot
// that throws must never be the reason a report didn't send.

const MAX_ERRORS = 8;
const recentErrors = [];
let connection = { connected: null, since: null };
let installed = false;

const clip = (s, n = 300) => String(s ?? '').slice(0, n);

function remember(kind, message, extra) {
  recentErrors.push({
    kind, message: clip(message), ...extra, at: new Date().toISOString(),
  });
  if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
}

/**
 * Called once from main.js: keeps the last few uncaught errors and the
 * Firebase connection state so a report can say what had just gone wrong.
 */
export function installBugContext(db) {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  try {
    window.addEventListener('error', (e) => {
      remember('error', e?.message || e?.error?.message, {
        source: e?.filename ? `${clip(e.filename, 120)}:${e.lineno || 0}:${e.colno || 0}` : undefined,
        stack: e?.error?.stack ? clip(e.error.stack, 800) : undefined,
      });
    });
    window.addEventListener('unhandledrejection', (e) => {
      const r = e?.reason;
      remember('unhandledrejection', r?.message || r, {
        stack: r?.stack ? clip(r.stack, 800) : undefined,
      });
    });
  } catch { /* best effort */ }
  try {
    if (db) {
      onValue(dbRef(db, '.info/connected'), (snap) => {
        connection = { connected: Boolean(snap.val()), since: new Date().toISOString() };
      }, () => {});
    }
  } catch { /* best effort */ }
}

export const recentErrorsForReport = () => recentErrors.slice();

// Exposed for tests.
export function resetBugContext() {
  recentErrors.length = 0;
  connection = { connected: null, since: null };
}
export const noteError = (message) => remember('handled', message, {});

// A room player's name falls back to their email when they have no display
// name (store/rooms.js) - other people's emails never go in a report.
export const safeName = (name) => {
  if (!name) return null;
  const s = String(name);
  return s.includes('@') ? '(email hidden)' : clip(s, 60);
};

const safe = (fn) => {
  try { return fn(); } catch (error) { return { snapshotError: clip(error?.message || error, 200) }; }
};

function openOverlays() {
  if (typeof document === 'undefined') return [];
  const found = [];
  const probes = {
    playMenu: '.menu',
    clueList: '.sheet__close',
    sheet: '.sheet-backdrop',
    chat: '.chat',
    howToPlay: '.howto-backdrop',
    adminPanel: '.approval-backdrop',
    bugResolutionNotice: '.bug-resolution-backdrop',
    installPrompt: '.install-backdrop',
  };
  for (const [name, sel] of Object.entries(probes)) {
    if (document.querySelector(sel)) found.push(name);
  }
  return found;
}

function puzzleSection(state, getters) {
  const { puzzle } = state;
  if (!puzzle) return null;
  const { cursor } = state;
  const clueOf = (dir) => {
    const e = clueAt(puzzle, cursor.row, cursor.col, dir);
    if (!e) return null;
    // The player's own letters in the word ("C_T"), never the answer.
    const typed = cellsOf(e, dir).map(([r, c]) => letterAt(state.letters, r, c) || '_').join('');
    return {
      id: e.num != null ? `${e.num}${dir === 'across' ? 'A' : 'D'}` : null,
      clue: clip(e.clue, 200),
      len: e.len,
      theme: Boolean(e.theme),
      typed,
    };
  };
  const filled = safe(() => getters.filled);
  const total = safe(() => getters.totalCells);
  return {
    date: puzzle.date || null,
    title: puzzle.title || null,
    weekday: puzzle.weekday || null,
    difficulty: puzzle.difficulty || null,
    size: puzzle.size || null,
    words: (puzzle.across?.length || 0) + (puzzle.down?.length || 0),
    cursor: { ...cursor },
    activeClue: safe(() => clueOf(cursor.dir)),
    crossClue: safe(() => clueOf(cursor.dir === 'across' ? 'down' : 'across')),
    filled,
    totalCells: total,
    wrongCount: safe(() => wrongCells(puzzle, state.letters, whiteCells(puzzle)).length),
    checkedWrongCount: Object.keys(state.checked || {}).length,
    revealedCount: Object.keys(state.reveals || {}).length,
    wasFull: state.wasFull,
    fullNotice: state.fullNotice,
    pencil: state.pencil,
    seconds: state.seconds,
    timerRunning: state.timerRunning,
    clockPaused: Boolean(state.clockPaused),
    solvedAt: state.solvedAt,
  };
}

function roomSection(state, getters) {
  if (!state.roomId) return null;
  const meta = state.roomMeta || {};
  const players = Object.entries(meta.players || {}).map(([uid, p]) => ({
    uid, name: safeName(p?.name), color: p?.color || null,
  }));
  return {
    roomId: state.roomId,
    roomCode: meta.code || meta.roomCode || null,
    date: meta.date || null,
    hostUid: meta.hostUid || null,
    startedAt: meta.startedAt || null,
    clock: meta.runFrom ? { banked: meta.banked || 0, runFrom: meta.runFrom, activeAt: meta.activeAt || null } : null,
    solvedAt: meta.solvedAt || null,
    metaLoaded: Boolean(state.roomMeta),
    playerCount: players.length,
    players,
    presentCount: Object.keys(state.presence || {}).length,
    othersPresent: safe(() => getters.others.map((o) => ({
      uid: o.uid, name: safeName(o.name), row: o.row ?? null, col: o.col ?? null, dir: o.dir || null,
    }))),
    myColor: safe(() => getters.myColor),
    chatMessages: state.chat ? state.chat.length : null,
    chatUnread: safe(() => getters.chatUnread),
    roomError: state.roomError || null,
  };
}

/**
 * The whole snapshot. `store` is the Vuex store; nothing here writes to it.
 */
export function gatherBugContext(store) {
  const out = {};
  const state = store?.state || {};
  const getters = store?.getters || {};
  const part = (name, fn) => { out[name] = safe(fn); };

  part('screen', () => {
    if (!getters.isApproved) return state.authReady ? 'siteGate' : 'loading';
    if (state.puzzleLoading) return 'openingPuzzle';
    if (state.puzzle) return state.solvedAt ? 'play (solved)' : 'play';
    return 'home';
  });
  part('openOverlays', openOverlays);
  part('reporter', () => ({
    uid: state.user?.uid || null,
    name: safeName(state.user?.displayName),
    siteStatus: state.siteUser?.status || null,
    isAdmin: Boolean(state.siteUser?.isAdmin),
  }));
  part('auth', () => ({
    authReady: state.authReady,
    siteUserReady: state.siteUserReady,
    firebaseConfigured: state.firebaseConfigured,
  }));
  part('game', () => state.game);
  part('puzzle', () => puzzleSection(state, getters));
  part('puzzleError', () => state.puzzleError || null);
  part('room', () => roomSection(state, getters));
  part('settings', () => ({
    ...state.settings,
    jumpToNextClue: getters.jumpToNextClue,
    wrapInWord: getters.wrapInWord,
  }));
  part('archive', () => ({
    indexLoaded: state.index != null,
    indexCount: state.index ? Object.keys(state.index).length : 0,
    myProgressDates: Object.keys(state.progressByDate || {}).length,
  }));
  part('timesCount', () => Object.keys(state.times || {}).length);
  part('network', () => ({
    navigatorOnline: typeof navigator !== 'undefined' ? navigator.onLine : null,
    firebaseConnected: connection.connected,
    firebaseConnectedSince: connection.since,
    connectionType: navigator?.connection?.effectiveType || null,
  }));
  part('device', () => ({
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    visualViewport: window.visualViewport
      ? `${Math.round(window.visualViewport.width)}x${Math.round(window.visualViewport.height)}`
      : null,
    coarsePointer: window.matchMedia?.('(pointer: coarse)').matches ?? null,
    standalone: Boolean(window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone),
    reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? null,
    orientation: window.screen?.orientation?.type || null,
    language: navigator.language || null,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    visibility: document.visibilityState || null,
    serviceWorkerControlled: Boolean(navigator.serviceWorker?.controller),
  }));
  part('build', () => ({
    version: process.env.VUE_APP_GIT_SHA || 'unknown',
    builtAt: process.env.VUE_APP_BUILD_TIME || null,
  }));
  part('recentErrors', recentErrorsForReport);
  return out;
}
